/**
 * `gamification-core` — CONFIG del dominio stockpilot (inventario).
 *
 * Retrofit OPT-IN y NO punitivo (B2B): la gamificación premia mantener el
 * inventario EN MOVIMIENTO y EXACTO (movimientos registrados, operaciones
 * confirmadas, conteos reconciliados, constancia) como ENGAGEMENT — nunca como
 * vigilancia del operario. Ligas privadas por defecto (§leagues). El motor es
 * agnóstico; aquí solo declaramos las reglas.
 *
 * La gamificación DERIVA del `activity` (hash-chain inmutable) — ver `derive.ts`.
 * No altera el log: es una proyección pura y recomputable. Solo se apoya en las
 * entidades que el write-path REALMENTE traza (ver `tracked_fields` en
 * core-rust): `stock_move` (append-only) y `stock_picking` (estado). El conteo
 * cíclico (`inventory_count`) NO se traza como entidad, pero su validación
 * MATERIALIZA `stock_move`s con `reason = "count-adjustment"` — de ahí derivamos
 * el evento de reconciliación de conteo sin tocar el servidor.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Tipos de evento de dominio (derivados del feed de `activity`). */
export const STOCKPILOT_EVENTS = {
  /** Operación (recepción/entrega/transferencia) confirmada: borrador→hecho. */
  pickingConfirmed: "picking.confirmed",
  /** Diferencia de un conteo cíclico reconciliada (move con reason count-adjustment). */
  countAdjusted: "count.adjusted",
  /** Movimiento de stock registrado (cualquier otro move append-only). */
  stockMoved: "stock.moved",
} as const;

/** Línea de racha: días manteniendo el inventario en movimiento (constancia). */
export const INVENTORY_STREAK_LINE = "inventory";

export const stockpilotGamificationConfig: GamificationConfig = {
  xp: {
    [STOCKPILOT_EVENTS.pickingConfirmed]: 25,
    [STOCKPILOT_EVENTS.countAdjusted]: 5,
    [STOCKPILOT_EVENTS.stockMoved]: 3,
  },
  streakLines: {
    // Cualquier movimiento de inventario (registrado o de conteo) marca el día.
    [STOCKPILOT_EVENTS.stockMoved]: [INVENTORY_STREAK_LINE],
    [STOCKPILOT_EVENTS.countAdjusted]: [INVENTORY_STREAK_LINE],
  },
  counters: {
    [STOCKPILOT_EVENTS.pickingConfirmed]: ["pickings_confirmed"],
    [STOCKPILOT_EVENTS.countAdjusted]: ["counts_adjusted"],
    [STOCKPILOT_EVENTS.stockMoved]: ["stock_moves"],
  },
  badges: [
    {
      id: "first-move",
      title: "First move",
      description: "Register your first stock movement.",
      when: counterAtLeast("stock_moves", 1),
    },
    {
      id: "mover",
      title: "Mover",
      description: "Register fifty stock movements.",
      when: counterAtLeast("stock_moves", 50),
    },
    {
      id: "accurate",
      title: "Accurate",
      description: "Reconcile ten cycle-count differences.",
      when: counterAtLeast("counts_adjusted", 10),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Keep inventory moving seven days in a row.",
      when: streakAtLeast(INVENTORY_STREAK_LINE, 7),
      bonusXp: 50,
    },
  ],
  quests: [
    {
      id: "daily-moves",
      title: "Five stock moves today",
      period: "daily",
      goals: [{ metric: STOCKPILOT_EVENTS.stockMoved, target: 5 }],
      bonusXp: 20,
    },
  ],
};
